import { useEffect, useRef, useState } from "react";
import { api, formatApiError, formatRelative, BACKEND_URL, getStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Paperclip, Upload, X, Image as ImageIcon, FileText, Loader2, Trash2, Globe,
} from "lucide-react";

const MAX_BYTES = 10 * 1024 * 1024;

const formatSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (mime) => mime && mime.startsWith("image/");

/**
 * AttachmentsPanel — drop-in upload/list/download UI.
 * @param parentType  "care_seeker" | "task" | "outreach"
 * @param parentId    The parent record id
 * @param mode        "list" (default) | "gallery" (renders images as thumbnails)
 * @param title       Section title (default: "Attachments")
 */
export default function AttachmentsPanel({
  parentType,
  parentId,
  mode = "list",
  title = "Attachments",
  embedded = false,
}) {
  const { can, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const canDelete = !!can && (can("users.edit") || user?.role === "admin" || user?.role === "executive_director");

  const load = async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/attachments", { params: { parent_type: parentType, parent_id: parentId } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [parentType, parentId]);

  const onFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const f of fileList) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} exceeds 10 MB limit`);
        continue;
      }
      const form = new FormData();
      form.append("parent_type", parentType);
      form.append("parent_id", parentId);
      form.append("file", f);
      try {
        await api.post("/attachments", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded += 1;
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
    setUploading(false);
    if (uploaded) toast.success(`${uploaded} file${uploaded > 1 ? "s" : ""} uploaded`);
    load();
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadAttachment = async (att) => {
    try {
      const token = getStoredToken();
      const res = await fetch(`${BACKEND_URL}/api/attachments/${att.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Download failed");
    }
  };

  const removeAttachment = async (att) => {
    if (!confirm(`Delete "${att.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Attachment deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePublic = async (att) => {
    try {
      const { data } = await api.patch(`/attachments/${att.id}`, { is_public: !att.is_public });
      setItems((prev) => prev.map((x) => (x.id === att.id ? { ...x, is_public: data.is_public } : x)));
      toast.success(data.is_public ? "Photo is now public" : "Photo is no longer public");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canPublish = mode === "gallery" && canDelete; // admin/ED only, outreach gallery only

  const images = mode === "gallery" ? items.filter((i) => isImage(i.content_type)) : [];
  const documents = mode === "gallery" ? items.filter((i) => !isImage(i.content_type)) : items;

  return (
    <div className={embedded ? "" : "bg-white border border-[#E5E5E0] rounded-2xl p-5"} data-testid={`attachments-panel-${parentType}`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
          <h3 className="font-display text-base font-semibold text-[#0F172A]">{title}</h3>
          {items.length > 0 && (
            <span className="text-xs text-[#1E3A8A]">· {items.length}</span>
          )}
        </div>
        <label
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors ${
            uploading ? "bg-[#E5E5E0] text-[#1E3A8A] cursor-wait" : "bg-[#1E3A8A] text-white hover:bg-[#172554]"
          }`}
          data-testid={`upload-trigger-${parentType}`}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={2} />}
          {uploading ? "Uploading…" : "Upload"}
          <input
            ref={inputRef}
            type="file"
            multiple
            disabled={uploading}
            onChange={(e) => onFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid={`upload-input-${parentType}`}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          />
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-[#1E3A8A] py-4">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#1E3A8A] py-2">No files yet. 10 MB max per file · PDF, images, DOCX, XLSX, TXT, CSV.</div>
      ) : (
        <>
          {mode === "gallery" && images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4" data-testid="attachments-gallery">
              {images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => downloadAttachment(img)}
                  data-testid={`gallery-item-${img.id}`}
                  className="group relative aspect-square rounded-xl overflow-hidden border border-[#E5E5E0] bg-[#F9F8F6]"
                  title={img.filename}
                >
                  <ImagePreview attachment={img} />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <div className="text-white text-[10px] truncate">{img.filename}</div>
                    <div className="text-white/70 text-[9px]">{img.uploaded_by_name} · {formatRelative(img.uploaded_at)}</div>
                  </div>
                  {img.is_public && (
                    <span
                      data-testid={`public-badge-${img.id}`}
                      className="absolute top-1 left-1 inline-flex items-center gap-1 bg-[#1E3A8A] text-white text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                    >
                      <Globe className="w-2.5 h-2.5" strokeWidth={2.5} /> Public
                    </span>
                  )}
                  {canPublish && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); togglePublic(img); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePublic(img);
                        }
                      }}
                      data-testid={`toggle-public-${img.id}`}
                      title={img.is_public ? "Remove from public gallery" : "Share in public gallery"}
                      className={`absolute bottom-9 right-1 inline-flex items-center justify-center w-7 h-7 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ${
                        img.is_public
                          ? "bg-[#1E3A8A] text-white hover:bg-[#172554]"
                          : "bg-black/50 text-white hover:bg-[#1E3A8A]"
                      }`}
                      aria-label={img.is_public ? "Unpublish photo" : "Publish photo"}
                    >
                      <Globe className="w-3.5 h-3.5" strokeWidth={2} />
                    </span>
                  )}
                  {canDelete && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); removeAttachment(img); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          removeAttachment(img);
                        }
                      }}
                      data-testid={`gallery-delete-${img.id}`}
                      className="absolute top-1 right-1 inline-flex items-center justify-center bg-black/50 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 cursor-pointer"
                      aria-label="Delete attachment"
                    >
                      <X className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {documents.length > 0 && (
            <ul className="divide-y divide-[#F0EFEB]" data-testid="attachments-list">
              {documents.map((att) => (
                <li key={att.id} className="py-2.5 flex items-center gap-3" data-testid={`attachment-row-${att.id}`}>
                  <div className="w-9 h-9 rounded-xl bg-[#F9F8F6] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                    {isImage(att.content_type) ? <ImageIcon className="w-4 h-4" strokeWidth={1.75} /> : <FileText className="w-4 h-4" strokeWidth={1.75} />}
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadAttachment(att)}
                    className="flex-1 min-w-0 text-left hover:text-[#1E3A8A] transition-colors"
                    data-testid={`download-attachment-${att.id}`}
                  >
                    <div className="text-sm font-medium text-[#0F172A] truncate">{att.filename}</div>
                    <div className="text-xs text-[#1E3A8A]">
                      {formatSize(att.size)} · {att.uploaded_by_name || "—"} · {formatRelative(att.uploaded_at)}
                    </div>
                  </button>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => removeAttachment(att)}
                      data-testid={`delete-attachment-${att.id}`}
                      className="p-2 rounded-full text-[#1E3A8A] hover:bg-[#FEE2E2] hover:text-[#991B1B] transition-colors flex-shrink-0"
                      aria-label="Delete attachment"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function ImagePreview({ attachment }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const token = getStoredToken();
        const res = await fetch(`${BACKEND_URL}/api/attachments/${attachment.id}/download`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setSrc(url);
      } catch { /* ignore */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [attachment.id]);
  if (!src) return <div className="absolute inset-0 flex items-center justify-center"><ImageIcon className="w-6 h-6 text-[#1E3A8A]" strokeWidth={1.5} /></div>;
  return <img src={src} alt={attachment.filename} className="absolute inset-0 w-full h-full object-cover" />;
}
